import { supabase } from '../supabase'

/**
 * The two single-player daily word games, FAKE OUT and CLOSE ENOUGH
 * (final names, owner call 2026-08-11). All scoring is server-side;
 * these calls return nothing that reveals an answer before it is
 * earned, and FAKE OUT is dealt one word at a time so the hidden
 * per-word clock stays honest.
 *
 * word_games_today() is also the visibility gate: it returns null per
 * game unless the server says the caller may see it (game flagged
 * live, or an admin account during the test period).
 */

export interface WordGamesToday {
  fake_out: {
    available: boolean
    entered: boolean
    score: number
    correct: number
    total: number
    week_total: number
  } | null
  close_enough: {
    available: boolean
    guesses: number
    hints: number
    solved: boolean
    score: number
    week_total: number
  } | null
}

export interface FakeoutResult {
  position: number
  word: string
  pick: string
  real_def: string
  correct: boolean
  secs: number
  pts: number
}

/** fakeout_state(): either the day's reveal, or the word in play. */
export interface FakeoutState {
  entered: boolean
  day: string
  available?: boolean
  // entered=true
  score?: number
  correct?: number
  total?: number
  detail?: FakeoutResult[]
  // entered=false, available=true
  position?: number
  word?: string
  defs?: string[]
  answered?: FakeoutResult[]
}

export interface FakeoutPickResponse {
  done: boolean
  result: FakeoutResult
  score: number
  correct?: number
  position?: number
  total: number
}

export interface CloseGuessResult {
  solved: boolean
  repeat: boolean
  guess?: string
  rank: number | null
  guessNo?: number
  guesses: number
  score?: number
  secret?: string
}

export interface CloseToday {
  day: string
  available: boolean
  guesses: number
  hints: number
  solved: boolean
  score: number
  secret: string | null
  list: { guess: string; rank: number | null; guessNo: number; isHint: boolean }[]
}

export interface CloseHintResult {
  word: string
  rank: number
  hints: number
}

export interface WordChartRow {
  rank: number
  profile_id: string
  display_name: string
  avatar_url: string | null
  total: number
  days_played: number
  is_me: boolean
}

async function rpc<T>(name: string, args?: Record<string, unknown>): Promise<T> {
  // RPCs are not in the generated database types yet — same cast the
  // weekly counts use.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data, error } = await (supabase as any).rpc(name, args)
  if (error) throw error
  return data as T
}

export const fetchWordGamesToday = () => rpc<WordGamesToday>('word_games_today')

export const fetchFakeoutState = () => rpc<FakeoutState>('fakeout_state')

export const fakeoutPick = (position: number, pick: string) =>
  rpc<FakeoutPickResponse>('fakeout_pick', { p_position: position, p_pick: pick })

export const closeenoughGuess = (guess: string) =>
  rpc<CloseGuessResult>('closeenough_guess', { p_guess: guess })

export const closeenoughHint = () => rpc<CloseHintResult>('closeenough_hint')

export const fetchCloseenoughToday = () => rpc<CloseToday>('closeenough_today')

export const fetchWordWeeklyChart = (game: 'fake_out' | 'close_enough') =>
  rpc<WordChartRow[]>('word_weekly_chart', { p_game: game })

export interface WordArchiveWeek {
  week_start: string
  week_end: string
  players: number
  winner_name: string | null
  winner_total: number | null
}

export interface WordArchiveChartRow {
  rank: number
  profile_id: string
  display_name: string
  avatar_url: string | null
  total: number
  days_played: number
  /** The caller's own row. It is returned even when ranked below ten. */
  is_me: boolean
}

export const fetchWordArchive = (game: 'fake_out' | 'close_enough') =>
  rpc<WordArchiveWeek[]>('word_weekly_archive', { p_game: game })

export const fetchWordArchiveChart = (game: 'fake_out' | 'close_enough', week: string) =>
  rpc<WordArchiveChartRow[]>('word_archive_chart', { p_game: game, p_week: week })
